import Player from '@vimeo/player';
import { debounce as _debounce } from 'es-toolkit';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

// Vimeo Player SDK:  https://developer.vimeo.com/player/sdk
const console = window.console;

export default class {

	constructor(options) {
		this.defaults = {
			el: false

			, background: true
			, autoplay: true

			, pauseOnStart: false
			, autopause: true

			, playPause: false
		}
		this.options = {};
		Object.assign(this.options, this.defaults, options);
		this.namespace = 'vimeoplayer';
		this.el = this.options.el;
		this.main = this.options.main;

		this.playClicked = false; // userClick!
		this.playing = false;

		if (this.options.playPause) {
			this.playPause = this.el.querySelector('.play-pause');
			if (this.playPause && this.playPause.parentElement.classList.contains('overlay-video')) {
				this.playPause = this.playPause.parentElement;
			}
		}

		if (this.el) {
			this.init();
		}
		else {
			this.onError({ name: 'missing element', message: 'You must pass the dom element to the vimeoPlayer constructor' });
		}
	}

	init() {
		this.iframe = this.el.querySelector('iframe');

		if (this.iframe) {

			// already add by php the class 'video-vimeo' ... just a formality
			this.isVimeo = this.iframe.getAttribute('src').indexOf('vimeo') !== -1;
			if (this.isVimeo) {
				// already add by php ... just a formality
				this.el.classList.add('video-vimeo');
				this.initVimeo(true);
			}

			this.addEvents(true);

			// if the placeholder (video-poster) is specified, set video embed to visible
			if (this.el.querySelector('.placeholder')) {
				this.el.classList.add('video-load');
			}

		}
		else {
			this.onError({ name: 'missing element', message: 'iframe is missing to vimeoPlayer' });
		}
	}

	initVimeo(b) {
		if (b) {

			console.log('initVimeo ');

			// Vimeo SDK init
			this.player = new Player(this.iframe);
			this.player.ready().then(
				() => this.onVideo('ready')
			).catch((error) => this.onError(error));
		}
		else {
			this.player.destroy().then(function () {
				// the player was destroyed
			}).catch((error) => this.onError(error));
		}
		if (this.options.autopause) {
			this.initAutoPause(b);
		}
	}

	initAutoPause(b) {
		if (b) {
			this.scrollTriggerId = this.namespace + '_' + Math.random();

			ScrollTrigger.create({
				id: this.scrollTriggerId
				, scrub: true
				, trigger: this.el
				, toggleClass: 'auto-in-view'
				, scroller: this.main.options.$scroll_el
				, onToggle: (e) => {
					this.forceAutoPause(!e.isActive);
				}
			});
		}
		else {
			ScrollTrigger.getById(this.scrollTriggerId).kill();
		}
	}

	//---------------------------------------- EVENTS ---------------------------------------//
	addEvents(b) {
		if (b) {

			if (this.isVimeo) {
				this.player.on('error', (e) => this.onVideo('error', e));
				this.player.on('loaded', (e) => this.onVideo('load', e));
				this.player.on('bufferend', (e) => this.onVideo('bufferend', e));
				this.player.on('play', (e) => this.onVideo('play', e));
				this.player.on('pause', (e) => this.onVideo('pause', e));
				this.player.on('ended', (e) => this.onVideo('ended', e));
			}

			if (this.options.playPause && this.playPause) {
				this._onPlayPauseClick = () => this.togglePlay();
				this.playPause.addEventListener('click', this._onPlayPauseClick);
			}

			this.btnSound = this.el.querySelector('.play-sound');
			if (this.btnSound) {
				this._onSoundClick = () => this.toggleSound();
				this.btnSound.addEventListener('click', this._onSoundClick);
			}

		}
		else {

			if (this.isVimeo) {
				this.player.off('error');
				this.player.off('loaded');
				this.player.off('bufferend');
				this.player.off('play');
				this.player.off('pause');
				this.player.off('ended');
			}

			if (this.options.playPause && this.playPause && this._onPlayPauseClick) {
				this.playPause.removeEventListener('click', this._onPlayPauseClick);
			}

			if (this.btnSound && this._onSoundClick) {
				this.btnSound.removeEventListener('click', this._onSoundClick);
			}

		}
		this.debounceResize(b);
	}

	debounceResize(b) {
		if (b) {
			this._debResize = _debounce(() => {
				this.onResize();
			}, 77);
			window.addEventListener('resize', this._debResize);

			this.onResize();
		}
		else {
			if (this._debResize) {
				this._debResize.cancel();
				window.removeEventListener('resize', this._debResize);
			}
		}
	}

	onVideo(state, e) {
		switch (state) {
			case 'error':
				this.onError({ name: 'video state', message: 'Video State Error' });
				this.onError(e);
				break;
			case 'ready':

				// this is fired when Vimeo SDK is ready
				if (this.options.autoplay && this.options.background) {
					this.player.setMuted(true).then(() => {
						this.play(true);
					}).catch((error) => this.onError(error));
				}

				break;
			case 'load':
				setTimeout(() => this.el.classList.add('video-load', 'video-ready'), this.options.background ? 1200 : 17);
				break;
			case 'bufferend':
				break;
			case 'play':
				this.el.classList.add('video-play');
				this.el.classList.remove('video-pause');
				this.playing = true;

				if (this.options.autoplay && this.options.pauseOnStart && !this.options.autopause) {
					setTimeout(() => this.play(false), 600);
					this.options.pauseOnStart = false;
				}
				break;
			case 'pause':
				this.el.classList.remove('video-play');
				this.el.classList.add('video-pause');
				this.playing = false;
				break;
			case 'ended':
				break;
		}
	}

	onError(error) {
		let str = this.namespace + ' : ';
		switch (error.name) {
			case 'PasswordError':
				str += 'The video is password-protected';
				break;
			case 'PrivacyError':
				str += 'The video is private';
				break;
			default:
				str += error.message;
		}
	}

	onResize() {
		const w_video = parseInt(this.iframe.getAttribute('width')),
			h_video = parseInt(this.iframe.getAttribute('height'));

		// size to fit the container avail space
		if ( this.el.classList.contains('video-size-cover') || this.el.classList.contains('video-size-fixed') ) {
			const container = this.el.parentElement,
				w = container.offsetWidth,
				h = container.offsetHeight,
				// get max avail proportion
				p = Math.max(w / w_video, h / h_video);

			this.iframe.style.width = Math.ceil(w_video * p) + 'px';
			this.iframe.style.height = Math.ceil(h_video * p) + 'px';

			// just in case is not already set with css
			container.style.overflow = 'hidden';
		}
		else if (this.el.classList.contains('video-size-auto')) {
			// autosize video by its proportions
			const embedWrapper = this.el.querySelector('.embed-wrapper');
			if (embedWrapper) {
				embedWrapper.style.paddingBottom = (h_video / w_video * 100) + '%';
			}
		}
		/*
		else if (this.el.classList.contains('video-size-fixed')) {
			// fixed size, do nothing
			// with css we size as horizontal/square/vertical
		}
		*/

		if (this.options.autopause) {
			ScrollTrigger.refresh();
		}
	}

	//---------------------------------------- METHODS ---------------------------------------//
	play(b) {
		if (b) {

			if (this.isVimeo) {
				this.player.play().then(() => {
					// The video is playing
				}).catch((error) => this.onError(error));
			}

		}
		else {

			if (this.isVimeo) {
				this.player.pause().then(() => {
					// The video is paused
				}).catch((error) => this.onError(error));
			}

		}
	}

	// fired from button play/pause
	togglePlay() {
		if (this.isVimeo) {
			this.player.getPaused().then((paused) => {
				this.play(paused);
				this.playClicked = paused;
			}).catch((error) => this.onError(error));
		}
	}

	// fired from button sound
	toggleSound() {
		if (this.isVimeo) {

			this.player.getVolume().then((volume) => {
				const v = volume === 0 ? 1 : 0;

				this.player.setVolume(v).then((newvolume) => {
					if (newvolume === 1) {
						this.btnSound.classList.remove('off');
						this.btnSound.classList.add('on');
					}
					else {
						this.btnSound.classList.remove('on');
						this.btnSound.classList.add('off');
					}
				}).catch((error) => this.onError(error));

			}).catch((error) => this.onError(error));

		}
	}

	// fired by ScrollTrigger if this.options.autopause is true
	forceAutoPause(b) {
		if (this.isVimeo) {
			if (this.options.background && this.options.autoplay) {
				// background mode autoplay, play/pause if in view/out of view
				this.play(!b);
			}
			else if (this.options.background && !this.options.autoplay && this.playClicked) {
				// set to mute only if the video is playing because user playClicked
				if (b) {
					this.play(false);
					this.playClicked = false;
				}
			}
			else {
				// normal embed, just pause if out of view
				if (b) {
					this.play(false);
				}
			}
		}
	}

	destroy() {
		this.addEvents(false);
		if (this.isVimeo) {
			this.initVimeo(false);
		}
	}

}
